import { state } from '../state.js';

const SCALE    = 2;
const W        = 800;
const COL_W    = 130;
const ROW_H    = 36;
const HEADER_H = 110;
const TH_H     = 58;
const PAD      = 28;

const C_PRIMARY      = '#003366';
const C_PRIMARY_DARK = '#001f33';
const C_CHECK        = '#2d6a4f';
const C_ABSENT       = '#d62828';
const C_DASH         = '#adb5bd';
const C_BORDER       = '#dee2e6';
const C_STRIPE       = '#f8f9fa';
const C_HEADER_BG    = '#eef2f7';

// ─── Canvas helpers ───────────────────────────────────────────────────────────

function drawCheck(ctx, cx, cy, s) {
    ctx.strokeStyle = C_CHECK;
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.38, cy + s * 0.05);
    ctx.lineTo(cx - s * 0.05, cy + s * 0.38);
    ctx.lineTo(cx + s * 0.42, cy - s * 0.28);
    ctx.stroke();
}

function drawDash(ctx, cx, cy) {
    ctx.strokeStyle = C_DASH;
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';
    ctx.beginPath();
    ctx.moveTo(cx - 10, cy);
    ctx.lineTo(cx + 10, cy);
    ctx.stroke();
}

function drawText(ctx, text, cx, cy, color, font) {
    ctx.fillStyle    = color;
    ctx.font         = font;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, cx, cy);
}

function truncate(ctx, text, maxW) {
    if (ctx.measureText(text).width <= maxW) return text;
    while (text.length > 3 && ctx.measureText(text + '…').width > maxW) {
        text = text.slice(0, -1);
    }
    return text + '…';
}

function loadImage(url) {
    return new Promise((resolve) => {
        const img     = new Image();
        img.crossOrigin = 'anonymous';
        img.onload  = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src     = url;
    });
}

// ─── Attendance symbol per cell ───────────────────────────────────────────────

function drawSymbol(ctx, cx, cy, group, topicIds, childId) {
    if (!group) { drawDash(ctx, cx, cy); return; }

    const td = group.topicDays.find(
        (d) => d.canceled == 0 && topicIds.some((id) => id == d.topic_id)
    );
    if (!td) { drawDash(ctx, cx, cy); return; }

    const rec = td.attendance.find((a) => a.child_id == childId);
    if (!rec) {
        drawDash(ctx, cx, cy);
    } else if (rec.attended === 1) {
        drawCheck(ctx, cx, cy, 13);
    } else if (rec.attended === 0) {
        drawText(ctx, 'A', cx, cy, C_ABSENT, 'bold 14px Arial,sans-serif');
    } else {
        drawText(ctx, '?', cx, cy, C_ABSENT, 'bold 14px Arial,sans-serif');
    }
}

// ─── Main canvas builder ──────────────────────────────────────────────────────

async function buildCanvas(childId) {
    const child = state.getChild(childId);
    if (!child) return null;

    const group   = state.getGroup(state.currentGroup);
    const levelId = group?.level_id;
    if (!levelId) return null;

    // Recompute cycles (same logic as report.js)
    const level    = state.data.levels.find((l) => l.id == levelId);
    const enrolled = level
        ? level.groups.filter((g) => g.children.some((c) => c.id == childId))
        : [];
    enrolled.sort((a, b) => {
        const dateA = a.topicDays?.length
            ? Math.min(...a.topicDays.map((td) => new Date(td.date + 'T00:00:00').getTime()))
            : 0;
        const dateB = b.topicDays?.length
            ? Math.min(...b.topicDays.map((td) => new Date(td.date + 'T00:00:00').getTime()))
            : 0;
        return dateA - dateB;
    });
    const cycles = [enrolled[0] || null, enrolled[1] || null, enrolled[2] || null];

    // Recompute topics (same logic as report.js)
    const byTitle = new Map();
    state.data.topics
        .filter((t) => t.level_id == levelId && t.atrium_day == 1)
        .sort((a, b) => a.order - b.order)
        .forEach((t) => {
            if (!byTitle.has(t.title)) byTitle.set(t.title, { topic: t, ids: new Set([t.id]) });
            else byTitle.get(t.title).ids.add(t.id);
        });
    const topics = Array.from(byTitle.values()).map(({ topic, ids }) => ({
        ...topic,
        ids: Array.from(ids),
    }));

    const topicColW = W - COL_W * 3;
    const H         = HEADER_H + TH_H + topics.length * ROW_H + PAD;

    const canvas    = document.createElement('canvas');
    canvas.width    = W * SCALE;
    canvas.height   = H * SCALE;
    const ctx       = canvas.getContext('2d');
    ctx.scale(SCALE, SCALE);

    // ── Background ────────────────────────────────────────────────────────────
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // ── Header gradient ───────────────────────────────────────────────────────
    const grad = ctx.createLinearGradient(0, 0, W, HEADER_H);
    grad.addColorStop(0, C_PRIMARY_DARK);
    grad.addColorStop(1, C_PRIMARY);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, HEADER_H);

    // ── Avatar ────────────────────────────────────────────────────────────────
    const ar = 36;
    const ax = PAD + ar;
    const ay = HEADER_H / 2;

    ctx.save();
    ctx.beginPath();
    ctx.arc(ax, ay, ar, 0, Math.PI * 2);
    ctx.closePath();

    const photo = child.photo_url ? await loadImage(child.photo_url) : null;
    if (photo) {
        ctx.clip();
        ctx.drawImage(photo, ax - ar, ay - ar, ar * 2, ar * 2);
    } else {
        ctx.fillStyle = '#1f6feb';
        ctx.fill();
        ctx.clip();
        ctx.fillStyle = '#ffffff';
        ctx.font         = 'bold 26px Arial,sans-serif';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
            `${child.first_name[0]}${child.last_name[0]}`.toUpperCase(),
            ax, ay
        );
    }
    ctx.restore();

    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth   = 2;
    ctx.beginPath();
    ctx.arc(ax, ay, ar, 0, Math.PI * 2);
    ctx.stroke();

    // ── Name ──────────────────────────────────────────────────────────────────
    const nameX = ax + ar + 18;
    ctx.fillStyle    = '#ffffff';
    ctx.font         = 'bold 24px Arial,sans-serif';
    ctx.textAlign    = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${child.first_name} ${child.last_name}`, nameX, ay - 9);



    // ── Table header (Topic + C1/C2/C3 en la misma fila) ─────────────────────
    let y = HEADER_H;
    ctx.fillStyle = C_HEADER_BG;
    ctx.fillRect(0, y, W, TH_H);
    ctx.strokeStyle = C_BORDER;
    ctx.lineWidth   = 2;
    ctx.beginPath();
    ctx.moveTo(0, y + TH_H);
    ctx.lineTo(W, y + TH_H);
    ctx.stroke();

    ctx.fillStyle    = '#444';
    ctx.font         = 'bold 13px Arial,sans-serif';
    ctx.textAlign    = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('Topic', PAD, y + TH_H / 2);

    [0, 1, 2].forEach((i) => {
        const cx    = topicColW + i * COL_W + COL_W / 2;
        const cycle = cycles[i];

        ctx.fillStyle    = C_PRIMARY;
        ctx.font         = 'bold 14px Arial,sans-serif';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`C${i + 1}`, cx, y + 18);

        if (cycle) {
            const grp = state.getGroup(cycle.id);
            ctx.font      = '11px Arial,sans-serif';
            ctx.fillStyle = '#666';
            ctx.fillText(truncate(ctx, grp?.title ?? '', COL_W - 8), cx, y + 38);
        }
    });

    // ── Rows ──────────────────────────────────────────────────────────────────
    y += TH_H;
    topics.forEach((topic, i) => {
        const ry = y + i * ROW_H;

        if (i % 2 === 1) {
            ctx.fillStyle = C_STRIPE;
            ctx.fillRect(0, ry, W, ROW_H);
        }

        ctx.strokeStyle = '#eeeeee';
        ctx.lineWidth   = 1;
        ctx.beginPath();
        ctx.moveTo(0, ry + ROW_H);
        ctx.lineTo(W, ry + ROW_H);
        ctx.stroke();

        ctx.fillStyle    = '#444';
        ctx.font         = '13px Arial,sans-serif';
        ctx.textAlign    = 'left';
        ctx.textBaseline = 'middle';
        const label = truncate(ctx, `${i + 1}) ${topic.title}`, topicColW - PAD * 2);
        ctx.fillText(label, PAD, ry + ROW_H / 2);

        cycles.forEach((grp, ci) => {
            const cx = topicColW + ci * COL_W + COL_W / 2;
            drawSymbol(ctx, cx, ry + ROW_H / 2, grp, topic.ids, childId);
        });
    });

    return canvas;
}

// ─── Public: share or download ────────────────────────────────────────────────

export async function shareReport(childId, btnEl) {
    const child = state.getChild(childId);
    if (!child) return;

    if (btnEl) {
        btnEl.disabled = true;
        btnEl.innerHTML = '<i class="bi bi-hourglass-split"></i>';
    }

    try {
        const canvas = await buildCanvas(childId);
        if (!canvas) return;

        const fileName = `${child.first_name}_${child.last_name}_report.png`
            .replace(/\s+/g, '_');

        canvas.toBlob(async (blob) => {
            const file = new File([blob], fileName, { type: 'image/png' });

            if (navigator.canShare?.({ files: [file] })) {
                try {
                    await navigator.share({ files: [file], title: `${child.first_name} ${child.last_name}` });
                } catch (err) {
                    if (err.name !== 'AbortError') fallbackDownload(canvas, fileName);
                }
            } else {
                fallbackDownload(canvas, fileName);
            }
        }, 'image/png');
    } finally {
        if (btnEl) {
            btnEl.disabled = false;
            btnEl.innerHTML = '<i class="bi bi-share"></i>';
        }
    }
}

function fallbackDownload(canvas, fileName) {
    const a  = document.createElement('a');
    a.href   = canvas.toDataURL('image/png');
    a.download = fileName;
    a.click();
}
